import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';

import { requestsService } from '../../services/requestsService';
import { firestoreService } from '../../services/firestoreService';

import ProviderProfile from './ProviderProfile';
import Loader from '../../components/requests/Loader';

const ProviderDashboard = ({ bookId, bookData }) => {
  const { user } = useAuth();
  
  // Provider setup  
  const [profile, setProfile] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(true);

  // Requests  
  const [requests, setRequests] = useState([]);
  const [loadingRequests, setLoadingRequests] = useState(true);

  // Offers sent by this provider  
  const [offers, setOffers] = useState([]);
  const [loadingOffers, setLoadingOffers] = useState(true);

  // UI state  
  const [activeTab, setActiveTab] = useState("requests");
  const [offerModalOpen, setOfferModalOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);

  const [offerData, setOfferData] = useState({
    valueProp: "",
    price: "",
    timeframe: ""
  });

  // ---------------------------------------------------------------------
  // Load Provider Profile
  // ---------------------------------------------------------------------
  const loadProfile = useCallback(async () => {
    setLoadingProfile(true);

    try {
      const prof = await requestsService.getProviderProfile(bookId, user?.uid);
      if (prof) setProfile(prof);
    } catch (e) {
      console.error("Failed to load provider profile", e);
    } finally {
      setLoadingProfile(false);
    }
  }, [bookId, user]);

  // ---------------------------------------------------------------------
  // Load Active Requests (optionally by category)
  // ---------------------------------------------------------------------
  const loadRequests = useCallback(async () => {
    setLoadingRequests(true);

    try {
      // For now, load all requests.  
      // Later you can filter by provider's category.
      //const reqs = await requestsService.getUserRequests(bookId, null);
      //setRequests(reqs.filter(r => r.status !== "closed"));
    } catch (e) {
      console.error("Failed to load requests", e);
    } finally {
      setLoadingRequests(false);
    }
  }, [bookId]);

  // ---------------------------------------------------------------------
  // Load Offers sent by this provider
  // ---------------------------------------------------------------------
  const loadOffers = useCallback(async () => {
    setLoadingOffers(true);
    try {
      const all = await requestsService.getOffersForProvider?.(bookId, user?.uid);
      if (all) setOffers(all);
    } catch (e) {
      console.error("Failed to load provider offers:", e);
    } finally {
      setLoadingOffers(false);
    }
  }, [bookId, user]);

  // Initial load  
  useEffect(() => {
    if (!user) return;
    loadProfile();
    loadRequests();
    loadOffers();
  }, [user, loadProfile, loadRequests, loadOffers]);

  // ---------------------------------------------------------------------
  // Provider registration (if profile missing)
  // ---------------------------------------------------------------------
  const registerProvider = async () => {
    const newProfile = {
      businessName: "My Business",
      category: "General Services",
      aboutText: "",
      services: [],
      portfolioImages: []
    };

    const id = await requestsService.createProviderProfile(bookId, user.uid, newProfile);
    setProfile({ id, ...newProfile });
  };

  // ---------------------------------------------------------------------
  // Offer submission
  // ---------------------------------------------------------------------
  const openOfferModal = (req) => {
    setSelectedRequest(req);
    setOfferData({
      valueProp: `Hi, I can assist with "${req.description}".`,
      price: "",
      timeframe: req.timeframe || ""
    });
    setOfferModalOpen(true);
  };

  const sendOffer = async () => {
    if (!selectedRequest) return;

    const formattedOffer = {
      ...offerData,
      providerName: profile.businessName,
      providerType: profile.category,
      status: "PENDING",
      createdAt: new Date().toISOString()
    };

    await requestsService.createOffer(bookId, selectedRequest.id, user.uid, formattedOffer);
    setOfferModalOpen(false);

    loadOffers(); // refresh offers
  };

  // ---------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------
  if (loadingProfile) {
    return (
      <div className="py-20">
        <Loader text="Loading your provider dashboard…" />
      </div>
    );
  }

  // If provider not registered: show onboarding
  if (!profile) {
    return (
      <div className="max-w-xl mx-auto text-center py-20 animate-fade-in">
        <h1 className="text-4xl font-extrabold text-slate-900 dark:text-white mb-4">
          Become a Provider on {bookData?.title || "OpenRequests"}
        </h1>

        <p className="text-slate-600 dark:text-slate-300 mb-8">
          Register your business profile to start receiving matched requests and sending offers.
        </p>

        <button
          className="px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-semibold"
          onClick={registerProvider}
        >
          Create My Provider Profile
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-10 animate-fade-in">

      {/* Header */}
      <div className="mb-10 text-center">
        <h1 className="text-4xl font-extrabold text-slate-900 dark:text-white">
          Provider Dashboard — {profile.businessName}
        </h1>
        <p className="text-slate-500 dark:text-slate-400 mt-2">
          Manage your offers, view available requests, and update your profile.
        </p>
      </div>

      {/* Tabs */}
      <div className="flex justify-center mb-8">
        {["requests", "sent", "profile"].map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-5 py-2 text-sm font-medium rounded-lg mx-2 ${
              activeTab === tab
                ? "bg-indigo-600 text-white"
                : "bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300"
            }`}
          >
            {tab === "requests" && "Available Requests"}
            {tab === "sent" && "Sent Offers"}
            {tab === "profile" && "My Profile"}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === "requests" && (
        <RequestsTab
          requests={requests}
          loading={loadingRequests}
          onSendOffer={openOfferModal}
        />
      )}

      {activeTab === "sent" && (
        <SentOffersTab offers={offers} loading={loadingOffers} />
      )}

      {activeTab === "profile" && (
        <ProviderProfile
          bookId={bookId}
          userId={user.uid}
          onClose={() => {}}
        />
      )}

      {/* Offer Modal */}
      {offerModalOpen && (
        <OfferModal
          offerData={offerData}
          setOfferData={setOfferData}
          onClose={() => setOfferModalOpen(false)}
          onSubmit={sendOffer}
          request={selectedRequest}
        />
      )}
    </div>
  );
};

export default ProviderDashboard;

/* ------------------------------------------------------------------
   Subcomponents
------------------------------------------------------------------ */

const RequestsTab = ({ requests, loading, onSendOffer }) => {
  if (loading)
    return <Loader text="Loading available requests…" />;

  if (requests.length === 0)
    return <EmptyState text="No active requests available at the moment." />;

  return (
    <div className="space-y-4">
      {requests.map(req => (
        <div
          key={req.id}
          className="p-5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-sm"
        >
          <h3 className="font-bold text-slate-900 dark:text-white">{req.description}</h3>
          <p className="text-sm text-slate-600 dark:text-slate-300 mt-2">
            Timeframe: {req.timeframe || "Flexible"}  
          </p>
          <p className="text-sm text-slate-600 dark:text-slate-300">
            Location: {req.location || "N/A"}
          </p>

          <button
            className="mt-4 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm"
            onClick={() => onSendOffer(req)}
          >
            Send Offer
          </button>
        </div>
      ))}
    </div>
  );
};

const SentOffersTab = ({ offers, loading }) => {
  if (loading)
    return <Loader text="Loading your sent offers…" />;

  if (offers.length === 0)
    return <EmptyState text="You have not sent any offers yet." />;

  return (
    <div className="space-y-4">
      {offers.map((offer, i) => (
        <div
          key={i}
          className="p-5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-sm"
        >
          <h3 className="font-bold text-slate-900 dark:text-white">{offer.offerTitle}</h3>
          <p className="text-sm text-slate-600 dark:text-slate-300 mt-2">{offer.description}</p>

          <div className="mt-3 text-xs text-slate-500 dark:text-slate-400">
            Status: {offer.status}
          </div>
        </div>
      ))}
    </div>
  );
};

const OfferModal = ({ offerData, setOfferData, onClose, onSubmit, request }) => {
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-slate-800 w-full max-w-lg rounded-xl shadow-xl p-6">
        <h3 className="text-xl font-bold mb-4 text-slate-900 dark:text-white">
          Send Offer to Request
        </h3>

        <p className="text-slate-500 dark:text-slate-400 mb-4">
          "{request.description}"
        </p>

        <label className="block text-sm mb-2 font-medium">Value Proposition</label>
        <textarea
          rows={4}
          value={offerData.valueProp}
          onChange={(e) => setOfferData({ ...offerData, valueProp: e.target.value })}
          className="w-full p-3 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 mb-4"
        />

        <label className="block text-sm mb-2 font-medium">Price Estimate</label>
        <input
          type="text"
          value={offerData.price}
          onChange={(e) => setOfferData({ ...offerData, price: e.target.value })}
          className="w-full p-3 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 mb-4"
        />

        <label className="block text-sm mb-2 font-medium">Timeframe</label>
        <input
          type="text"
          value={offerData.timeframe}
          onChange={(e) => setOfferData({ ...offerData, timeframe: e.target.value })}
          className="w-full p-3 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 mb-6"
        />

        <div className="flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-slate-600 dark:text-slate-300">
            Cancel
          </button>

          <button
            onClick={onSubmit}
            className="px-6 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg"
          >
            Send Offer
          </button>
        </div>
      </div>
    </div>
  );
};

const EmptyState = ({ text }) => (
  <div className="text-center py-20 text-slate-500 dark:text-slate-400">
    {text}
  </div>
);
